import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Rating,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ProgressIcon,
  CreditCard as PaymentIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LandingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    // Page Title
    document.title = "Adilesson - UK Driving Instructor Scheduling & Syllabus Tracking App";

    // Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Manage your driving school like a modern business. Automated WhatsApp lesson reminders, UK DVSA syllabus tracking, calendar bookings, and online payments for ADI driving instructors.');

    // Open Graph Meta Tags
    const ogTags = [
      { property: 'og:title', content: 'Adilesson - Driving Instructor Scheduler' },
      { property: 'og:description', content: 'Automated WhatsApp reminders, calendar bookings, and DVSA syllabus tracking.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // JSON-LD Schema Markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Adilesson",
      "operatingSystem": "All",
      "applicationCategory": "BusinessApplication",
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "GBP",
        "lowPrice": "10.00",
        "highPrice": "15.00",
        "price": "10.00"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "120"
      }
    };
    
    let schemaScript = document.getElementById('jsonld-schema') as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'jsonld-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.text = JSON.stringify(schema);

    return () => {
      const script = document.getElementById('jsonld-schema');
      if (script) script.remove();
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Testimonials', href: '#testimonials' }
  ];

  const features = [
    {
      icon: <CalendarIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Smart Scheduling',
      description: 'Instructors can easily generate availability slots. Students view and book open times instantly. No more double bookings or phone tag.'
    },
    {
      icon: <ProgressIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      title: 'DVSA Progress Tracking',
      description: 'Fully mapped to the UK DVSA driving syllabus. Students see completed topics marked in green with ratings so they know exactly when they are ready to pass.'
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
      title: 'WhatsApp Notifications',
      description: 'Automated booking confirmations and scheduled reminder dispatches sent directly from the instructor’s connected WhatsApp. Built-in delay guards prevent spam filters.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
      title: 'Online Payments',
      description: 'Hassle-free PayPal checkout. Instructors can activate subscription billing (monthly/yearly) to run their driving school like a modern business.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Connect WhatsApp Device',
      description: 'Instructors scan a secure QR code or type a pairing code to link their WhatsApp instance via WhapiCell.'
    },
    {
      number: '02',
      title: 'Publish Availability',
      description: 'Instructors generate time slots on their dashboard calendar. Students are assigned and immediately notified.'
    },
    {
      number: '03',
      title: 'Book & Track Progress',
      description: 'Students choose a slot, book online, write lesson notes, and review completed UK DVSA topics step-by-step.'
    }
  ];

  const pricingPlans = [
    {
      title: 'Base Plan',
      price: '£10.00',
      period: 'per month',
      description: 'Core calendar management and DVSA syllabus tracking.',
      features: [
        'Unlimited Students & Bookings',
        'Smart Availability Calendar',
        'UK DVSA Syllabus Tracker',
        'Student Profile Pages',
        'Email Notifications Support',
        'PayPal Payment Checkout',
        'WhatsApp Module (Locked)'
      ],
      ctaText: 'Get Started',
      popular: false,
      note: 'Or £99/year (Save 17%)'
    },
    {
      title: 'Premium Plan',
      price: '£15.00',
      period: 'per month',
      description: 'Our full suite including automated WhatsApp automation.',
      features: [
        'Everything in the Base Plan',
        'WhatsApp Automated Alerts',
        'Automated Lesson Reminders',
        'WhapiCell QR Device Pairing',
        'Direct WhatsApp Message Dispatch',
        'Priority Customer Support'
      ],
      ctaText: 'Unlock Full Power',
      popular: true,
      note: 'Or £149/year (Save 17%)'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Jenkins',
      role: 'ADI Driving Instructor',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      comment: 'adilessons has completely transformed my workflow. My students book their own slots, and the automated WhatsApp reminders mean zero no-shows! Highly recommended.'
    },
    {
      name: 'David Miller',
      role: 'Newly Licensed Student',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      comment: 'Being able to track my DVSA syllabus topics and knowing exactly what I needed to work on made passing my driving test on the first try a breeze!'
    },
    {
      name: 'James O\'Connor',
      role: 'Independent Instructor',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      comment: 'The PayPal subscription billing is seamless. I no longer have to ask students for bank transfers. It runs on autopilot while I focus on lessons.'
    }
  ];

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'Admin') return '/admin/dashboard';
    if (user.role === 'Instructor') return '/instructor/dashboard';
    return '/student/dashboard';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fcfcfc', color: '#37474f' }}>
      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-12px); }
            100% { transform: translateY(0px); }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.15; }
            50% { transform: scale(1.05); opacity: 0.25; }
            100% { transform: scale(1); opacity: 0.15; }
          }
          html {
            scroll-behavior: smooth;
          }
        `}
      </style>

      {/* Dynamic Background Glows */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 650, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', top: -150, right: -100, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(25, 118, 210, 0.12) 0%, rgba(255,255,255,0) 70%)', animation: 'pulse 8s infinite ease-in-out' }} />
        <Box sx={{ position: 'absolute', top: 150, left: -200, width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(156, 39, 176, 0.08) 0%, rgba(255,255,255,0) 70%)', animation: 'pulse 12s infinite ease-in-out' }} />
      </Box>

      {/* Sticky Header Navbar */}
      <AppBar position="sticky" sx={{ background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(16px)', color: '#1a237e', boxShadow: '0 2px 16px rgba(0, 0, 0, 0.05)', zIndex: 1100 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 72 }}>
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1976d2', fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: '900', color: '#1a237e', letterSpacing: '-0.5px' }}>
                adi<span style={{ color: '#1976d2' }}>lessons</span>
              </Typography>
            </Box>

            {!isMobile && (
              <Box display="flex" gap={4} alignItems="center">
                {navItems.map((item) => (
                  <Typography
                    key={item.label}
                    component="a"
                    href={item.href}
                    sx={{
                      textDecoration: 'none',
                      color: '#455a64',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      transition: 'color 0.2s',
                      '&:hover': { color: '#1976d2' }
                    }}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Box>
            )}

            <Box display="flex" gap={2} alignItems="center">
              {isAuthenticated ? (
                <Button
                  variant="contained"
                  onClick={() => navigate(getDashboardPath())}
                  sx={{ borderRadius: 2.5, px: 3, fontWeight: 'bold', textTransform: 'none', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' }}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  {!isMobile && (
                    <Button
                      variant="text"
                      onClick={() => navigate('/login')}
                      sx={{ color: '#1a237e', fontWeight: 'bold', textTransform: 'none' }}
                    >
                      Sign In
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{ borderRadius: 2.5, px: 3, fontWeight: 'bold', textTransform: 'none', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)' }}
                  >
                    Get Started
                  </Button>
                </>
              )}

              {isMobile && (
                <IconButton onClick={handleDrawerToggle} edge="end" sx={{ color: '#1a237e' }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Navigation */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 260 } }}
      >
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="primary">Navigation</Typography>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <Typography
                component="a"
                href={item.href}
                onClick={handleDrawerToggle}
                sx={{
                  display: 'block',
                  width: '100%',
                  py: 1.5,
                  px: 3,
                  textDecoration: 'none',
                  color: '#37474f',
                  fontWeight: '600',
                  '&:hover': { backgroundColor: '#f5f5f5', color: '#1976d2' }
                }}
              >
                {item.label}
              </Typography>
            </ListItem>
          ))}
        </List>
        {!isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" gap={1.5} sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => { handleDrawerToggle(); navigate('/login'); }}>
                Sign In
              </Button>
              <Button fullWidth variant="contained" onClick={() => { handleDrawerToggle(); navigate('/register'); }}>
                Get Started
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 }, zIndex: 1, position: 'relative' }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Box display="inline-flex" alignItems="center" gap={1} sx={{ px: 2, py: 0.8, borderRadius: 5, bgcolor: 'rgba(25, 118, 210, 0.08)', color: '#1976d2', mb: 3 }}>
                <SpeedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Next-Gen Driving School Software
                </Typography>
              </Box>

              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2, color: '#1a237e', fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' } }}>
                Manage Your Driving Lessons with{' '}
                <span style={{ background: 'linear-gradient(45deg, #1976d2 30%, #00bcd4 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  adilessons
                </span>
              </Typography>

              <Typography variant="h6" sx={{ color: '#546e7a', fontWeight: '500', lineHeight: 1.6, mb: 4.5, fontSize: { xs: '1rem', md: '1.15rem' } }}>
                Shift your driving school into high gear. The ultimate scheduling, DVSA progress tracking, and automated WhatsApp reminder platform built specifically for independent ADIs.
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ py: 2, px: 4.5, borderRadius: 3, fontWeight: 'bold', fontSize: '1.05rem', textTransform: 'none', boxShadow: '0 8px 20px rgba(25, 118, 210, 0.25)', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('features');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ py: 2, px: 4.5, borderRadius: 3, fontWeight: 'bold', fontSize: '1.05rem', textTransform: 'none', color: '#1a237e', borderColor: '#b0bec5', '&:hover': { borderColor: '#1a237e', backgroundColor: 'rgba(26, 35, 126, 0.02)' } }}
                >
                  Explore Features
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 520,
                animation: 'float 6s ease-in-out infinite',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '10%',
                  left: '10%',
                  right: '-5%',
                  bottom: '-5%',
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(156, 39, 176, 0.1) 100%)',
                  borderRadius: '24px',
                  zIndex: -1,
                  filter: 'blur(16px)'
                }
              }}
            >
              <Box
                component="img"
                src="/drvman_hero.png"
                alt="drvman Application Dashboard Mockup"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '24px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
                  border: '8px solid rgba(255, 255, 255, 0.95)',
                  display: 'block'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Trust Stats Bar */}
      <Box sx={{ py: 5, borderTop: '1px solid #eceff1', borderBottom: '1px solid #eceff1', bgcolor: 'rgba(25, 118, 210, 0.01)', zIndex: 1, position: 'relative' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: '800', color: '#1976d2' }}>10k+</Typography>
              <Typography variant="body2" sx={{ color: '#78909c', fontWeight: '600', mt: 0.5 }}>Active Students</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: '800', color: '#1a237e' }}>500+</Typography>
              <Typography variant="body2" sx={{ color: '#78909c', fontWeight: '600', mt: 0.5 }}>ADIs Registered</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: '800', color: '#2e7d32' }}>250k+</Typography>
              <Typography variant="body2" sx={{ color: '#78909c', fontWeight: '600', mt: 0.5 }}>Lessons Booked</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: '800', color: '#9c27b0' }}>99.8%</Typography>
              <Typography variant="body2" sx={{ color: '#78909c', fontWeight: '600', mt: 0.5 }}>Notification Delivery</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, zIndex: 1, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: '800', color: '#1a237e', mb: 2 }}>
              Everything You Need to Manage Your Lessons
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a', fontWeight: '500' }}>
              Designed to take the headache out of scheduling and learning to drive. Features designed specifically for ADIs and UK students.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid #eceff1',
                    p: 2.5,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: '#1976d2',
                      boxShadow: '0 10px 24px rgba(25, 118, 210, 0.06)',
                      transform: 'translateY(-6px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ mb: 2, display: 'inline-flex', p: 1.5, borderRadius: 3, bgcolor: '#f4f6f8' }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#546e7a', lineHeight: 1.55 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f5f7f8', zIndex: 1, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: '800', color: '#1a237e', mb: 2 }}>
              How adilessons Works
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a', fontWeight: '500' }}>
              A seamless flow that connects instructors and students to provide absolute planning clarity.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: '#fff',
                    border: '1px solid #eceff1',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 10,
                      fontWeight: '900',
                      color: '#f0f4f8',
                      fontSize: '5rem',
                      userSelect: 'none',
                      zIndex: 0
                    }}
                  >
                    {step.number}
                  </Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#546e7a', lineHeight: 1.6 }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, zIndex: 1, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: '800', color: '#1a237e', mb: 2 }}>
              Simple, Transparent Pricing for Instructors
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a', fontWeight: '500' }}>
              Choose a subscription level that works for you. Students can create profiles and book lessons completely for free.
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={4} justifyContent="center">
            {pricingPlans.map((plan, idx) => (
              <Box key={idx} sx={{ width: { xs: '100%', sm: '80%', md: '45%', lg: '40%' }, maxWidth: '400px' }}>
                <Card
                  elevation={plan.popular ? 8 : 1}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: plan.popular ? '2px solid #1976d2' : '1px solid #eceff1',
                    borderRadius: 4,
                    position: 'relative',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-6px)'
                    }
                  }}
                >
                  {plan.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 24,
                        transform: 'translateY(-50%)',
                        bgcolor: '#1976d2',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      Most Popular
                    </Box>
                  )}

                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2, color: '#1a237e' }}>
                      {plan.title}
                    </Typography>
                    <Box display="flex" alignItems="baseline" sx={{ mb: 0.5 }}>
                      <Typography variant="h3" component="div" sx={{ fontWeight: '800', color: '#1976d2' }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#78909c', ml: 1 }}>
                        /{plan.period}
                      </Typography>
                    </Box>
                    {plan.note && (
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'block', mb: 1.5 }}>
                        {plan.note}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#546e7a', mb: 3 }}>
                      {plan.description}
                    </Typography>

                    <Divider sx={{ my: 2.5 }} />

                    <List sx={{ p: 0 }}>
                      {plan.features.map((feature, fIdx) => (
                        <ListItem key={fIdx} disableGutters sx={{ py: 0.8 }}>
                          <ProgressIcon sx={{ color: '#2e7d32', fontSize: 18, mr: 1.5, flexShrink: 0 }} />
                          <ListItemText primary={feature} primaryTypographyProps={{ sx: { fontSize: '0.9rem', color: '#455a64', fontWeight: '500' } }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>

                  <Box sx={{ p: 4, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={plan.popular ? 'contained' : 'outlined'}
                      size="large"
                      onClick={() => navigate('/register')}
                      sx={{
                        py: 1.5,
                        borderRadius: 2.5,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: plan.popular ? '0 4px 12px rgba(25, 118, 210, 0.2)' : 'none'
                      }}
                    >
                      {plan.ctaText}
                    </Button>
                  </Box>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box id="testimonials" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f5f7f8', zIndex: 1, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: '800', color: '#1a237e', mb: 2 }}>
              Loved by Instructors & Students Alike
            </Typography>
            <Typography variant="body1" sx={{ color: '#546e7a', fontWeight: '500' }}>
              Here is how our platform helps driving schools operate smoothly day in and day out.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((t, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: '1px solid #eceff1',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Rating value={t.rating} readOnly size="small" sx={{ mb: 2 }} />
                    <Typography variant="body2" sx={{ color: '#546e7a', lineHeight: 1.6, fontStyle: 'italic', mb: 3 }}>
                      "{t.comment}"
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={t.avatar} alt={t.name} sx={{ width: 48, height: 48, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                        {t.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78909c' }}>
                        {t.role}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Footer Section */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#1a237e', color: '#fff', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Background glow */}
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(25, 118, 210, 0.2) 0%, rgba(255,255,255,0) 60%)', zIndex: 0, pointerEvents: 'none' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: '800', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            Ready to Take the Wheel?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 'medium', mb: 4.5, maxWidth: 600, mx: 'auto', fontSize: { xs: '0.95rem', md: '1.15rem' } }}>
            Join hundreds of qualified ADIs and students using adilessons to simplify scheduling and track progress. Start today.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              py: 2,
              px: 5,
              borderRadius: 3,
              fontWeight: 'bold',
              fontSize: '1.05rem',
              textTransform: 'none',
              backgroundColor: '#1976d2',
              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Get Started for Free
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, bgcolor: '#0d132a', color: '#90a4ae', borderTop: '1px solid #1c2a4a' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <CarIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: '900', color: '#fff', letterSpacing: '-0.5px' }}>
                  adi<span style={{ color: '#1976d2' }}>lessons</span>
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ maxWidth: 360, lineHeight: 1.6 }}>
                adilessons is a comprehensive driving lesson booking and progress tracking platform built for independent ADIs and student drivers.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>Product</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Typography component="a" href="#features" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Features</Typography>
                <Typography component="a" href="#how-it-works" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Workflow</Typography>
                <Typography component="a" href="#pricing" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Pricing Plans</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>Support</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Typography component="a" href="mailto:support@drivemanagement.com" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Contact Support</Typography>
                <Typography component="a" href="/terms" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Terms & Conditions</Typography>
                <Typography component="a" href="/privacy" sx={{ color: 'inherit', textDecoration: 'none', fontSize: '0.85rem', '&:hover': { color: '#fff' } }}>Privacy Policy</Typography>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: '#1c2a4a', my: 3 }} />
          <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="caption">
              © {new Date().getFullYear()} adilessons. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', gap: 2 }}>
              <span>Designed in the UK</span>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
